import { useLingui } from "@lingui/react/macro";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { deducedError, useErrorMessageAlert } from "@/Utils/ErrorUtils";
import { FilledButton, OutlinedButton } from "@medoma/components/Button/Button";
import { useForm, FormProvider } from "react-hook-form";
import styles from "./Pause.module.scss";
import type { IAdmittedPatient } from "@medoma/models/patients";
import { patientStatusSchema } from "@medoma/models/patients";
import type { IActivity } from "@/api/Activities";
import { patientActiveActivitiesExceptForOneTimeActivitiesBeforeTodayQueryOptions } from "@/api/Activities";
import { Loading } from "@medoma/components/Loading/Loading";
import { pausePatient, useParentRoutesPatient } from "@/api/Patients";
import { useNavigate } from "react-router";
import Form from "@/components/Form/Form";
import { Text } from "@medoma/components/Text/Text";
import { Trans } from "@lingui/react/macro";
import { useEffect, useState } from "react";
import RadioButtons from "@medoma/components/RadioButton/RadioButtons";
import RadioButton from "@medoma/components/RadioButton/RadioButton";
import InputField from "@medoma/components/InputField/InputField";
import {
  calendarDateSchema,
  machineFormat,
} from "@medoma/models/date-and-time";
import { startOfToday, subDays } from "date-fns";
import { ActivitiesToCloseOrReactivate } from "@/pages/commandcenter/Patients/Patient/CareEpisode/ActivitiesToCloseOrReactivate";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { CareEpisodeSubpage } from "../CareEpisodeSubpage";
import subpageStyles from "../CareEpisodeSubpage.module.scss";
import { PauseReview } from "./PauseReview";

export const PauseSection = () => {
  const { t } = useLingui();
  const patient = useParentRoutesPatient();

  return (
    <CareEpisodeSubpage title={t`Pausa`}>
      <div className={subpageStyles.subPageContainer}>
        {patient.status === patientStatusSchema.enum.admitted ? (
          <Pause patient={patient} />
        ) : (
          <Text element="p">
            <Trans>Endast inskrivna patienter kan pausas</Trans>
          </Text>
        )}
      </div>
    </CareEpisodeSubpage>
  );
};

type IPauseFormData = {
  activityIds: string[];
  pauseDate: string;
  timeOfPause: "today" | "earlier";
};

const Pause = ({ patient }: { patient: IAdmittedPatient }) => {
  const { t } = useLingui();

  const {
    data: activities,
    isPending: isActivitiesPending,
    isError,
  } = useQuery(
    patientActiveActivitiesExceptForOneTimeActivitiesBeforeTodayQueryOptions({
      patientId: patient.id,
    }),
  );

  if (isActivitiesPending) {
    return <Loading message={t`Hämtar information för paus`} />;
  }

  // Avoid "jumpy" UI for when a user returns to this component
  // (and might have previously seen a different text - see PauseForm component)
  const reRenderKey = activities?.length;

  return (
    <PauseForm
      patient={patient}
      activities={activities ?? []}
      key={reRenderKey}
      errorFetchingActivities={isError}
    />
  );
};

const PauseForm = ({
  patient,
  activities,
  errorFetchingActivities,
}: {
  patient: IAdmittedPatient;
  activities: IActivity[];
  errorFetchingActivities: boolean;
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useLingui();
  const { displayErrorMessageAlert } = useErrorMessageAlert();

  const { mutate: pausePatientMutation, isPending: isPausing } = useMutation({
    mutationFn: ({
      patientId,
      pauseDate,
      activitiesToClose,
    }: {
      patientId: string;
      pauseDate: Date;
      activitiesToClose?: string[];
    }) => pausePatient({ patientId, pauseDate, activitiesToClose }),
    onError: (error) => {
      displayErrorMessageAlert(
        `${t`Det gick inte att pausa.`} ${deducedError(error)}`,
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries();
      void navigate("..", { relative: "path" });
    },
  });

  const methods = useForm<IPauseFormData>({
    defaultValues: {
      pauseDate: machineFormat(new Date(), "yyyy-MM-dd"),
      timeOfPause: "today",
      // One-time activities are left unchecked so the user opts in explicitly
      activityIds: activities
        .filter(({ recurring }) => recurring)
        .map(({ id }) => id),
    },
  });

  const {
    formState: { errors },
    handleSubmit,
    register,
    watch,
    resetField,
  } = methods;

  // Acceptable. We don't use react compiler, we just lint to find incompatibilities, such as this.
  // oxlint-disable-next-line react/incompatible-library
  const timeOfPause = watch("timeOfPause");

  useEffect(() => {
    if (timeOfPause === "today") {
      resetField("pauseDate");
    }
  }, [timeOfPause, resetField]);

  const retroactivePauseDateSpan = {
    from: machineFormat(patient.admittedAt, "yyyy-MM-dd"),
    to: machineFormat(subDays(startOfToday(), 1), "yyyy-MM-dd"),
  };
  const canPauseOnEarlierDate =
    retroactivePauseDateSpan.from <= retroactivePauseDateSpan.to;

  const [pauseToReview, setPauseToReview] = useState<IPauseFormData | null>(
    null,
  );

  const validateAndSubmit = handleSubmit((formData) => {
    setPauseToReview(formData);
  });

  const numberOfActiveActivities = activities.length;

  return (
    <div className={styles.pauseContent}>
      <FormProvider {...methods}>
        <Form onSubmit={validateAndSubmit}>
          <RadioButtons legend={t`Pausad från`} orientation="horizontal">
            <RadioButton
              label={{ text: t`Idag` }}
              {...register("timeOfPause")}
              value={"today"}
              visualStyle="framed"
            />
            <RadioButton
              label={{ text: t`Tidigare datum` }}
              {...register("timeOfPause")}
              value={"earlier"}
              disabled={!canPauseOnEarlierDate}
              visualStyle="framed"
            />
          </RadioButtons>
          {timeOfPause === "earlier" ? (
            <InputField
              label={t`Tidigare datum`}
              {...register("pauseDate", {
                required: true,
                validate: (pauseDate, { timeOfPause }) => {
                  if (timeOfPause === "today") {
                    return true;
                  }
                  if (pauseDate > retroactivePauseDateSpan.to) {
                    return t`Välj ett datum före idag`;
                  }
                  if (pauseDate < retroactivePauseDateSpan.from) {
                    return t`Pausdatumet kan inte vara före inskrivningsdatumet`;
                  }
                  return true;
                },
              })}
              type="date"
              errorMessage={errors.pauseDate?.message}
              showOptionalLabel={false}
            />
          ) : null}
          {errorFetchingActivities ? (
            <ErrorMessage
              message={t`Kunde inte hämta information om ej avslutade aktiviteter. Det går fortfarande att pausa, men det kan finnas aktiviteter som inte är avslutade.`}
            />
          ) : activities.length > 0 ? (
            <ActivitiesToCloseOrReactivate
              activities={activities}
              legend={t`Välj de aktiviteter som ska avslutas vid paus`}
            />
          ) : null}

          {numberOfActiveActivities > 0 ? (
            <div>
              <Text element="p">
                <Trans>
                  Aktiviteter som avslutas kan skapas igen automatiskt vid
                  återupptagning.
                </Trans>
              </Text>
            </div>
          ) : (
            <Text element="p">
              <Trans>Alla patientens aktiviteter är avslutade.</Trans>
            </Text>
          )}

          <div className={styles.actions}>
            <OutlinedButton asLinkTo=".." relative="path">
              {t`Tillbaka`}
            </OutlinedButton>
            <FilledButton type="submit">{t`Granska paus`}</FilledButton>
          </div>
        </Form>
      </FormProvider>
      {pauseToReview ? (
        <PauseReview
          patient={patient}
          pauseDate={pauseToReview.pauseDate}
          activitiesToCloseCount={pauseToReview.activityIds.length}
          activitiesToKeepCount={
            activities.length - pauseToReview.activityIds.length
          }
          disableSubmitButton={isPausing}
          onCancel={() => {
            // Closing the review can't stop a pause that is already sent
            if (isPausing) {
              return;
            }
            setPauseToReview(null);
          }}
          onPause={() => {
            pausePatientMutation({
              patientId: patient.id,
              pauseDate: calendarDateSchema.decode(pauseToReview.pauseDate),
              activitiesToClose: pauseToReview.activityIds,
            });
          }}
        />
      ) : null}
    </div>
  );
};
